<script setup lang="ts">
type Side = 'top' | 'bottom' | 'left' | 'right'

interface Props {
  wl?: number
  amp?: number
  th?: number
  color?: string
  speed?: number
  display?: 'inline-block' | 'block' | 'inline-flex' | 'flex'
  sides?: Side[]
  tag?: string
}

const props = withDefaults(defineProps<Props>(), {
  wl: 32,
  amp: 4,
  th: 2,
  color: '#FDC02D',
  speed: 2,
  display: 'inline-block',
  sides: () => ['top', 'bottom', 'left', 'right'],
  tag: 'span',
})

const showTop = computed(() => props.sides.includes('top'))
const showBottom = computed(() => props.sides.includes('bottom'))
const showLeft = computed(() => props.sides.includes('left'))
const showRight = computed(() => props.sides.includes('right'))

const tileThickness = computed(() => props.amp * 2 + props.th)

function encode(svg: string) {
  return encodeURIComponent(svg).replace(/'/g, '%27').replace(/"/g, '%22')
}

const horizontalSvg = computed(() => {
  const { wl, amp, th, color } = props
  const h = tileThickness.value
  const mid = h / 2
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${wl}' height='${h}' viewBox='0 0 ${wl} ${h}'><path d='M0 ${mid} Q${wl * 0.25} ${mid - amp} ${wl * 0.5} ${mid} Q${wl * 0.75} ${mid + amp} ${wl} ${mid}' stroke='${color}' stroke-width='${th}' fill='none' stroke-linecap='round'/></svg>`
})

const verticalSvg = computed(() => {
  const { wl, amp, th, color } = props
  const w = tileThickness.value
  const mid = w / 2
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${wl}' viewBox='0 0 ${w} ${wl}'><path d='M${mid} 0 Q${mid - amp} ${wl * 0.25} ${mid} ${wl * 0.5} Q${mid + amp} ${wl * 0.75} ${mid} ${wl}' stroke='${color}' stroke-width='${th}' fill='none' stroke-linecap='round'/></svg>`
})

const cssVars = computed(() => ({
  '--wb-wl': `${props.wl}px`,
  '--wb-th': `${tileThickness.value}px`,
  '--wb-speed': `${props.speed}s`,
  '--wb-wl-neg': `-${props.wl}px`,
  '--wb-h-bg': `url("data:image/svg+xml,${encode(horizontalSvg.value)}")`,
  '--wb-v-bg': `url("data:image/svg+xml,${encode(verticalSvg.value)}")`,
}))

const displayClass = computed(() => {
  switch (props.display) {
    case 'block': return 'block'
    case 'flex': return 'flex'
    case 'inline-flex': return 'inline-flex'
    default: return 'inline-block'
  }
})
</script>

<template>
  <component :is="tag" :class="['wavy-border relative', displayClass]" :style="cssVars">
    <slot />
    <span v-if="showTop" class="wavy-side wavy-top" aria-hidden="true" />
    <span v-if="showBottom" class="wavy-side wavy-bottom" aria-hidden="true" />
    <span v-if="showLeft" class="wavy-side wavy-left" aria-hidden="true" />
    <span v-if="showRight" class="wavy-side wavy-right" aria-hidden="true" />
  </component>
</template>

<style scoped>
.wavy-side {
  position: absolute;
  pointer-events: none;
}

.wavy-top,
.wavy-bottom {
  left: 0;
  right: 0;
  height: var(--wb-th);
  background-image: var(--wb-h-bg);
  background-size: var(--wb-wl) var(--wb-th);
  background-repeat: repeat-x;
  animation: wb-slide-h var(--wb-speed) linear infinite;
}

.wavy-top {
  top: 0;
  transform: translateY(-50%);
}

.wavy-bottom {
  bottom: 0;
  transform: translateY(50%);
}

.wavy-left,
.wavy-right {
  top: 0;
  bottom: 0;
  width: var(--wb-th);
  background-image: var(--wb-v-bg);
  background-size: var(--wb-th) var(--wb-wl);
  background-repeat: repeat-y;
  animation: wb-slide-v var(--wb-speed) linear infinite;
}

.wavy-left {
  left: 0;
  transform: translateX(-50%);
}

.wavy-right {
  right: 0;
  transform: translateX(50%);
}
</style>

<style>
@keyframes wb-slide-h {
  from { background-position-x: 0; }
  to   { background-position-x: var(--wb-wl-neg); }
}

@keyframes wb-slide-v {
  from { background-position-y: 0; }
  to   { background-position-y: var(--wb-wl-neg); }
}
</style>
